/**
 * Phone nav is a CSS checkbox drawer. A same-page hash (`#contact`) does not
 * uncheck it, and the click sits inside an overflow panel, so the browser
 * scrolls that panel instead of the page. Close first, then scroll the target.
 */

export function sameDocumentSectionHash(href: string): string | null {
  const trimmed = href.trim();
  const hashAt = trimmed.indexOf('#');
  if (hashAt < 0) {
    return null;
  }
  const hash = trimmed.slice(hashAt);
  if (hash.length < 2) {
    return null;
  }
  if (trimmed.startsWith('#')) {
    return hash;
  }
  if (/^\/(?:[a-z]{2})?#/i.test(trimmed)) {
    return hash;
  }
  return null;
}

export function closeNavDrawerFrom(node: Element): void {
  const toggle = node.closest('.ws-nav')?.querySelector('.ws-nav__toggle-input');
  if (toggle && 'checked' in toggle) {
    (toggle as HTMLInputElement).checked = false;
  }
}

/** Returns true when the click was a drawer hash with a live target. */
export function activateNavDrawerHashLink(anchor: Element): boolean {
  if (typeof anchor.closest !== 'function' || !anchor.closest('.ws-nav__drawer')) {
    return false;
  }
  const href = anchor.getAttribute('href');
  if (!href || typeof document === 'undefined') {
    return false;
  }
  const hash = sameDocumentSectionHash(href);
  if (!hash) {
    return false;
  }
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    /* keep the raw fragment */
  }
  const target = document.getElementById(id);
  if (!target) {
    return false;
  }
  closeNavDrawerFrom(anchor);
  target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  if (typeof history !== 'undefined' && typeof history.pushState === 'function') {
    history.pushState(null, '', hash);
  }
  return true;
}

/** Published HTML has no React click handler; one listener covers every arrangement. */
export const NAV_DRAWER_HASH_SCRIPT = `<script>
(function () {
  if (window.__wsNavDrawerHash) return;
  window.__wsNavDrawerHash = true;
  document.addEventListener('click', function (event) {
    var node = event.target;
    if (!node || !node.closest) return;
    var anchor = node.closest('a[href]');
    if (!anchor || !anchor.closest('.ws-nav__drawer')) return;
    var href = (anchor.getAttribute('href') || '').trim();
    var hashAt = href.indexOf('#');
    if (hashAt < 0) return;
    var hash = href.slice(hashAt);
    if (hash.length < 2) return;
    var same = href.charAt(0) === '#' || /^\\/(?:[a-z]{2})?#/i.test(href);
    if (!same) return;
    var id = hash.slice(1);
    try { id = decodeURIComponent(id); } catch (e) {}
    var target = document.getElementById(id);
    if (!target) return;
    event.preventDefault();
    var toggle = anchor.closest('.ws-nav') && anchor.closest('.ws-nav').querySelector('.ws-nav__toggle-input');
    if (toggle) toggle.checked = false;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (window.history && history.pushState) history.pushState(null, '', hash);
  });
})();
</script>`;
